/**
 * The custom React Flow node — what the prototype called a "node card."
 *
 * Renders a 240px-wide card with:
 *   - Header strip: type label (Start/Decision/Outcome) + entry badges (variant count,
 *     entry effects, choice count)
 *   - Optional row of objective-color dots
 *   - Title
 *   - Two-line preview of the fallback variant's sceneText
 *   - List of choices, each with badges for conditional visibility,
 *     destination-chain length, feedback-chain length, effects, and objective dots
 *   - An issue dot in the top-left corner when validation has anything to say
 *
 * Handles:
 *   - One target handle on top-center (every node accepts incoming edges).
 *   - One source handle per choice on the bottom, fanned out by index — same
 *     fan layout the prototype used for its SVG arrows.
 *
 * This component receives everything it needs via `data` (see adapters.ts);
 * it doesn't reach into the store. That keeps it cheap for React Flow's
 * re-render machinery and easy to test in isolation later.
 */
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { GripVertical, ChevronRight, GitBranch, Layers } from 'lucide-react';
import type { ScenarioFlowNode } from './adapters';
import { objectiveColor } from '../panel/ObjectivePalette';

const NODE_W = 240;

const TYPE_STYLES = {
  start: {
    bg: '#F0EDE5',
    border: '#999388',
    text: '#3D3935',
    label: 'Start',
  },
  decision: {
    bg: '#E8EEF4',
    border: '#6B8CAE',
    text: '#2C4E6B',
    label: 'Decision',
  },
  outcome: {
    bg: '#E8F0E5',
    border: '#6B8C5C',
    text: '#3D5C2C',
    label: 'Outcome',
  },
} as const;

// Palette + objectiveColor live in `panel/ObjectivePalette.ts` since the
// editor uses them in two places (canvas dots, ObjectiveTagsEditor chips).
// Single source of truth.

export function ScenarioNodeCard({ data, selected }: NodeProps<ScenarioFlowNode>) {
  const { node, isStart, scenario, errorCount, warningCount } = data;
  const style = TYPE_STYLES[node.type];

  // Fallback variant — the unconditional last entry, what the canvas previews.
  const variants = node.variants || [];
  const fallback = variants[variants.length - 1] || { sceneText: '', media: [] };

  const variantCount = variants.length;
  const hasEntryEffects = (node.entryEffects || []).length > 0;
  const issueColor = errorCount > 0 ? '#A14A38' : warningCount > 0 ? '#B8551F' : null;
  const choiceCount = node.choices.length;
  const nodeObjectiveTags = node.objectiveTags || [];

  return (
    <div
      className="relative rounded-md shadow-sm font-sans select-none"
      style={{
        width: NODE_W,
        background: style.bg,
        border: `1.5px solid ${selected ? style.text : style.border}`,
        boxShadow: selected
          ? `0 0 0 3px ${style.bg}, 0 4px 12px rgba(0,0,0,0.08)`
          : '0 2px 4px rgba(0,0,0,0.04)',
        color: style.text,
      }}
    >
      {/* Target handle: incoming edges land at the top-center. */}
      <Handle
        type="target"
        position={Position.Top}
        id="in"
        style={{
          background: style.border,
          width: 8,
          height: 8,
          border: 'none',
          opacity: node.type === 'start' ? 0 : 0.7,
        }}
        isConnectable={node.type !== 'start'}
      />

      {/* Issue indicator dot in the top-left corner. */}
      {issueColor && (
        <div
          className="absolute -top-1.5 -left-1.5 w-3.5 h-3.5 rounded-full border-2"
          style={{ background: issueColor, borderColor: '#FAF7F0' }}
          title={`${errorCount} error${errorCount === 1 ? '' : 's'}, ${warningCount} warning${warningCount === 1 ? '' : 's'}`}
        />
      )}

      {/* Header strip */}
      <div
        className="flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-widest"
        style={{ borderBottom: `1px solid ${style.border}50` }}
      >
        <span className="flex items-center gap-1">
          <GripVertical size={10} className="opacity-50" />
          {style.label}
          {isStart && ' • Entry'}
        </span>
        <span className="flex items-center gap-1.5 opacity-60">
          {variantCount > 1 && (
            <span className="flex items-center gap-0.5" title={`${variantCount} variants`}>
              <Layers size={9} /> {variantCount}
            </span>
          )}
          {hasEntryEffects && (
            <span style={{ fontSize: 9 }} title="Has entry effects">
              fx
            </span>
          )}
          <span>
            {choiceCount} choice{choiceCount === 1 ? '' : 's'}
          </span>
        </span>
      </div>

      {/* Objective tag dots — color-coded per objective. */}
      {nodeObjectiveTags.length > 0 && (
        <div className="flex items-center gap-1 px-3 pt-1.5">
          {nodeObjectiveTags.map((tagId) => {
            const obj = scenario.objectives && scenario.objectives[tagId];
            if (!obj) return null;
            return (
              <span
                key={tagId}
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: objectiveColor(scenario.objectives, tagId) }}
                title={`${obj.code}: ${obj.description}`}
              />
            );
          })}
        </div>
      )}

      {/* Title + scene-text preview */}
      <div className="px-3 py-2">
        <div className="text-sm font-bold leading-tight">{node.title || 'Untitled'}</div>
        <div className="text-xs mt-1 line-clamp-2 opacity-70">
          {fallback.sceneText || <span className="italic">No scene text yet</span>}
        </div>
      </div>

      {/* Choice list */}
      {choiceCount > 0 && (
        <div className="px-3 pb-2 space-y-0.5">
          {node.choices.map((c, i) => {
            const conditional = !!c.visibleIf;
            const branched = (c.destinations || []).length > 1;
            const hasFx = (c.effects || []).length > 0;
            const feedbackBranched = (c.feedbackVariants || []).length > 1;
            const choiceTags = c.objectiveTags || [];
            const badgeText = [
              hasFx && 'fx',
              branched && `+${(c.destinations.length - 1)}d`,
              feedbackBranched && `+${(c.feedbackVariants.length - 1)}fb`,
            ]
              .filter(Boolean)
              .join(' ');
            const badgeTitle = [
              hasFx && 'has effects',
              branched && `${c.destinations.length} destinations`,
              feedbackBranched && `${c.feedbackVariants.length} feedback variants`,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={c.id || i}
                className="text-[11px] flex items-center gap-1 truncate opacity-85"
              >
                {conditional ? <GitBranch size={10} /> : <ChevronRight size={10} />}
                <span className="truncate">
                  {c.label || <span className="italic">empty</span>}
                </span>
                {choiceTags.length > 0 && (
                  <span className="flex items-center gap-0.5 flex-shrink-0 ml-0.5">
                    {choiceTags.map((tagId) => {
                      const obj = scenario.objectives && scenario.objectives[tagId];
                      if (!obj) return null;
                      return (
                        <span
                          key={tagId}
                          className="w-1 h-1 rounded-full"
                          style={{ background: objectiveColor(scenario.objectives, tagId) }}
                          title={`${obj.code}: ${obj.description}`}
                        />
                      );
                    })}
                  </span>
                )}
                {badgeText && (
                  <span
                    className="ml-auto flex-shrink-0 text-[9px] opacity-60"
                    title={badgeTitle}
                  >
                    {badgeText}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Source handles: one per choice, fanned across the bottom. Each handle's
          id matches the choice id so edges can specify their sourceHandle. */}
      {node.choices.map((c, i) => {
        const fanCount = node.choices.length;
        // Position as a percentage; (i+1)/(fanCount+1) gives evenly fanned positions
        // matching the prototype's SVG arrow source-point math.
        const leftPct = ((i + 1) / (fanCount + 1)) * 100;
        return (
          <Handle
            key={c.id}
            type="source"
            position={Position.Bottom}
            id={c.id}
            style={{
              background: style.border,
              width: 8,
              height: 8,
              border: 'none',
              left: `${leftPct}%`,
              opacity: 0.7,
            }}
          />
        );
      })}
    </div>
  );
}
